import React, { useCallback } from 'react';
import type { FC } from 'react';
import { useIntl, defineMessages } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { EditorActions } from '@atlaskit/editor-core/actions';
import { ComposableEditor } from '@atlaskit/editor-core/composable-editor';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { SmartCardProvider } from '@atlaskit/link-provider/smart-card-provider';
import { token } from '@atlaskit/tokens';

import { useSnippetEditorPreset } from '@confluence/editor-presets/entry-points/useSnippetEditorPreset.ts';
import type { SnippetScope } from '@confluence/fabric-media-support/entry-points/snippetScope.ts';
import { withSmartCardProviderContext } from '@confluence/editor-presets/entry-points/withSmartCardProviderContext.ts';
import { withEditorActions } from '@confluence/editor-features/entry-points/withEditorActions.ts';
import { expValEquals } from '@confluence/feature-experiments';

const messages = defineMessages({
	editorPlaceholder: {
		id: 'snippets.snippetEditor.placeholder',
		defaultMessage: 'Edit your template content',
		description: 'Placeholder text for the snippet editor when it is empty',
	},
});

const styles = cssMap({
	editorWrapper: {
		height: '100%',
		overflowY: 'hidden',
		scrollbarColor: `${token('color.border.input')} transparent`,
	},
});

export type SnippetEditorProps = {
	/** Initial ADF document (e.g. from selection). */
	defaultValue: DocNode | object | undefined;
	/** Called when the document changes so the parent can submit the latest ADF. */
	onDocumentChange: (doc: DocNode) => void;
	/** Optional: called when the editor encounters an error (e.g. invalid ADF). */
	onError?: (error: Error | null) => void;
	/** Content ID for the snippet editor (for media uploads) */
	contentId: string;
	/** Space ID for the snippet editor (for mentions, context identifier) */
	spaceId: string;
	/** Space key for the snippet editor (for autoformatting) */
	spaceKey: string;
	/** Snippet ID when editing/duplicating an existing snippet (uses snippet media provider) */
	snippetId?: string;
	/** Snippet scope — when GLOBAL, skips snippet-specific media token fetch */
	snippetScope?: SnippetScope;
	/** Injected by withEditorActions HOC — required for bodied sync block support */
	editorActions: EditorActions;
};

const SnippetEditorComponent: FC<SnippetEditorProps> = ({
	defaultValue,
	onDocumentChange,
	onError,
	contentId,
	spaceId,
	spaceKey,
	snippetId,
	snippetScope,
	editorActions,
}) => {
	const intl = useIntl();
	const placeholder = intl.formatMessage(messages.editorPlaceholder);

	const {
		preset,
		editorApi,
		mentionProvider,
		contextIdentifierProvider,
		activityProvider,
		searchProvider,
		featureFlags,
	} = useSnippetEditorPreset({
		placeholder,
		contentId,
		spaceId,
		spaceKey,
		snippetId,
		snippetScope,
		editorActions,
	});

	const handleChange = useCallback(
		(editorView: EditorView) => {
			if (expValEquals('platform_editor_nest_table_in_panel', 'isEnabled', true)) {
				editorApi?.core?.actions.requestDocument((doc) => {
					if (doc) {
						onDocumentChange(doc as DocNode);
						onError?.(null);
					} else {
						onError?.(new Error('No adf document returned from editor'));
					}
				});
			} else {
				try {
					const doc = editorView.state.doc.toJSON();
					onDocumentChange({ ...doc, version: 1 } as DocNode);
					onError?.(null);
				} catch (error) {
					onError?.(error);
				}
			}
		},
		[editorApi, onDocumentChange, onError],
	);

	return (
		<SmartCardProvider>
			<div css={styles.editorWrapper}>
				<ComposableEditor
					preset={preset}
					defaultValue={defaultValue}
					appearance="full-page"
					mentionProvider={mentionProvider}
					contextIdentifierProvider={contextIdentifierProvider}
					activityProvider={activityProvider}
					searchProvider={searchProvider}
					onChange={handleChange}
					featureFlags={featureFlags}
					contentComponents={undefined}
					disabled={false}
					shouldFocus={false}
					contextPanel={undefined}
				/>
			</div>
		</SmartCardProvider>
	);
};

const SnippetEditorWithEditorActions = withEditorActions(SnippetEditorComponent);
export const SnippetEditor: (props: any) => React.JSX.Element = withSmartCardProviderContext(
	SnippetEditorWithEditorActions,
);
